import { useEffect, useState } from 'react'
import { ChevronDown, ChevronRight, Plus, Trash2, HelpCircle, Lock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { maxFollowUpsFor, maxSequenceStepsFor } from '@dealcloser/shared'
import { useLimitsPlan } from '@/hooks/use-plan'
import type {
  OutreachStrategy,
  SequenceStep,
  SequenceStepType,
} from './types'
import { nextFollowUpType, stepTypeAllowed } from './sequence-steps'
import { LEAD_PLACEHOLDERS, VariableTextarea } from './variable-textarea'

const STEP_TYPES: Array<{ value: SequenceStepType; label: string; hint: string }> = [
  {
    value: 'connection_request',
    label: 'Connection Request',
    hint: 'Plain LinkedIn invite (no note attached) sent to the lead first. They have to accept before any DMs can land.',
  },
  {
    value: 'first_dm',
    label: 'First DM',
    hint: 'The first direct message after the lead accepts the connection. This is your warm intro, written in your voice.',
  },
  {
    value: 'follow_up_1',
    label: 'Follow-up 1',
    hint: 'First nudge if the lead did not reply to the first DM. The AI never re-pitches here. It drops a new angle, a sourced observation, or a restated question.',
  },
  {
    value: 'follow_up_2',
    label: 'Follow-up 2',
    hint: 'Second nudge. On senior leads this is where the pitch is revealed as natural context. On mid/junior leads the AI names the product directly and proposes a call.',
  },
  {
    value: 'follow_up_3',
    label: 'Follow-up 3',
    hint: 'Last directive touch before the breakup message. Direct yes/no or invitation-to-be-proven-wrong close. Calendar offer allowed.',
  },
  {
    value: 'follow_up_4',
    label: 'Follow-up 4',
    hint: 'Pro. Another fresh angle, never a repeat. Often the place for proof, such as a real result, when trust is the gap.',
  },
  {
    value: 'follow_up_5',
    label: 'Follow-up 5',
    hint: 'Pro. Whatever step is last in your sequence gets the door-open close, with your calendar link.',
  },
  {
    value: 'follow_up_6',
    label: 'Follow-up 6',
    hint: 'Custom plan. A fresh angle; the last step in your sequence gets the door-open close.',
  },
  {
    value: 'follow_up_7',
    label: 'Follow-up 7',
    hint: 'Custom plan. A fresh angle; the last step in your sequence gets the door-open close.',
  },
]

/**
 * Message-angle selection (owner directive 2026-08-31): users are never
 * forced to pick a strategy. Every step defaults to 'auto' — the workers
 * resolve 'auto' to a concrete angle per lead (deterministic-random), so the
 * AI intelligently chooses and randomizes. Power users can still pin one
 * angle per step behind the collapsed "Advanced: message angle" control.
 * (History: per-step pickers were removed 2026-08-23 in favor of fixed
 * per-type defaults; those defaults are superseded by 'auto'.)
 */
const AUTO_STRATEGY: OutreachStrategy = 'auto'

/** The concrete angles a power user can pin, plus Auto. */
const ANGLE_OPTIONS: ReadonlyArray<{ value: OutreachStrategy; label: string; hint: string }> = [
  {
    value: 'auto',
    label: 'Auto (recommended)',
    hint: 'The AI picks the best angle for each lead and varies it across the campaign.',
  },
  { value: 'curiosity_gap', label: 'Curiosity gap', hint: 'Opens a loop the lead wants closed.' },
  { value: 'pain_agitate', label: 'Pain point', hint: 'Names a pain the lead likely feels.' },
  { value: 'mutual_value', label: 'Mutual value', hint: 'Leads with what is in it for them.' },
  { value: 'social_proof', label: 'Social proof', hint: 'Leans on results and peers.' },
]

const MAX_SAMPLES_PER_SLOT = 3
const MAX_SAMPLE_LEN = 600

/**
 * Hard lock — the first 2 steps of EVERY sequence are fixed to:
 *   index 0: connection_request
 *   index 1: first_dm
 *
 * Why: LinkedIn outreach via Unipile only works in the connect → accept → DM
 * flow. The first DM writes into the chat LinkedIn creates ONLY after the
 * connection request is accepted. Allowing customers to reorder/skip these
 * produces silently-broken campaigns. The backend enforces the same rule —
 * see workers/campaigns/src/routes/sequence.ts:assertLockedFirstSteps.
 */
const LOCKED_STEP_COUNT = 2
const LOCKED_STEP_TYPES: readonly SequenceStepType[] = ['connection_request', 'first_dm']

const DEFAULT_STEP_FOR_INDEX: Record<number, SequenceStepType> = {
  0: 'connection_request',
  1: 'first_dm',
}

const DEFAULT_STEP: SequenceStep = {
  type: 'follow_up_1',
  delay_days: 3,
  strategy: AUTO_STRATEGY,
  style_samples: [],
}

type SequenceBuilderProps = {
  readonly steps: readonly SequenceStep[]
  readonly onChange: (steps: readonly SequenceStep[]) => void
  /**
   * Show an optional writing-sample box on each message step (saved as
   * steps[i].style_samples; the campaign saves style_samples_mode
   * 'per_step'). "One set for all steps" was retired 2026-10-28 (plan N4);
   * campaigns saved with it keep working, see edit-sequence-dialog.tsx.
   */
  readonly showSamples?: boolean
  /** Hide the per-step AI angle pin (template mode: the text is fixed, no angle applies). */
  readonly hideAngle?: boolean
}

/**
 * Helper: edit a single sample inside an array, returning a new array.
 * If `index === array.length`, appends a new element. Empty trailing slots
 * are tolerated so the user can type into them.
 */
function setArrayElement(arr: readonly string[], index: number, value: string): string[] {
  const next = [...arr]
  next[index] = value
  return next
}

function removeArrayElement(arr: readonly string[], index: number): string[] {
  return arr.filter((_, i) => i !== index)
}

/** One sample textarea with variable chips, plus its remove button. */
function SampleSlot({
  value,
  index,
  placeholder,
  onChange,
  onRemove,
}: {
  readonly value: string
  readonly index: number
  readonly placeholder: string
  readonly onChange: (next: string) => void
  readonly onRemove: () => void
}) {
  return (
    <div className="flex items-start gap-2">
      <div className="flex-1">
        <VariableTextarea
          value={value}
          placeholder={placeholder}
          maxLength={MAX_SAMPLE_LEN}
          className="min-h-[60px] max-h-[140px] text-xs"
          onChange={onChange}
        />
      </div>
      {value.trim().length > 0 && (
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive mt-1"
          onClick={onRemove}
          aria-label={`Remove sample ${index + 1}`}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      )}
    </div>
  )
}

/**
 * Stable per-strategy sample editor. The number of slots is fixed at
 * MAX_SAMPLES_PER_SLOT so users can fill in 1, 2, or 3 freely without
 * needing to click "add". Empty slots are filtered out before persisting.
 */
function StyleSamplesEditor({
  label,
  hint,
  samples,
  onChange,
}: {
  readonly label: string
  readonly hint: string
  readonly samples: readonly string[]
  readonly onChange: (samples: readonly string[]) => void
}) {
  // Pad with empty strings so we always render MAX slots.
  const padded: string[] = [...samples]
  while (padded.length < MAX_SAMPLES_PER_SLOT) padded.push('')

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-0.5">
        <Label className="text-xs">{label}</Label>
        <p className="text-[11px] text-muted-foreground">{hint}</p>
      </div>
      <div className="flex flex-col gap-1 rounded-md border border-dashed bg-muted/20 px-2.5 py-2">
        <p className="text-[11px] font-medium text-muted-foreground">
          Variables for the lead's data, replaced at send time. Click one under a box to drop it in,
          or type <code className="font-mono">{'{'}</code> in the message to pick from the list.
        </p>
        <div className="flex flex-wrap gap-1.5">
          {LEAD_PLACEHOLDERS.map((token) => (
            <code
              key={token}
              className="rounded bg-background px-1.5 py-0.5 text-[10px] font-mono text-secondary-foreground"
            >
              {token}
            </code>
          ))}
        </div>
      </div>
      {padded.map((value, i) => (
        <SampleSlot
          key={i}
          value={value}
          index={i}
          placeholder={
            i === 0
              ? 'e.g. "Hey {first_name}, saw {company} just expanded into APAC, how are you handling the new hiring load?"'
              : `Sample ${i + 1} (max ${MAX_SAMPLE_LEN} chars)`
          }
          onChange={(text) => {
            const next = setArrayElement(padded, i, text)
            // Trim trailing empties so we don't persist meaningless slots.
            while (next.length > 0 && (next[next.length - 1] ?? '').trim() === '') {
              next.pop()
            }
            onChange(next)
          }}
          onRemove={() => onChange(removeArrayElement(padded, i).filter((s) => s !== ''))}
        />
      ))}
    </div>
  )
}

/**
 * Collapsed-by-default "Advanced: message angle" affordance. The primary
 * flow never asks for a strategy (every step ships as 'auto'); expanding
 * this reveals a small select for power users who want to pin one angle
 * for the step. Opens pre-expanded when an angle is already pinned so an
 * existing pin is never hidden.
 */
function StepAngleControl({
  strategy,
  onChange,
}: {
  readonly strategy: OutreachStrategy
  readonly onChange: (strategy: OutreachStrategy) => void
}) {
  const [open, setOpen] = useState(strategy !== AUTO_STRATEGY)

  // A stored strategy outside the curated list (legacy campaigns) must still
  // render — surface it as an extra option instead of a blank select.
  const options = ANGLE_OPTIONS.some((o) => o.value === strategy)
    ? ANGLE_OPTIONS
    : [
        ...ANGLE_OPTIONS,
        { value: strategy, label: strategy.replace(/_/g, ' '), hint: '' },
      ]
  const current = options.find((o) => o.value === strategy)

  return (
    <div className="flex flex-col gap-1.5 border-t pt-2">
      <button
        type="button"
        className="inline-flex items-center gap-1 self-start text-[11px] text-muted-foreground hover:text-foreground"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        Advanced: message angle
        {!open && strategy !== AUTO_STRATEGY && (
          <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium">
            {current?.label ?? strategy}
          </span>
        )}
      </button>
      {open && (
        <div className="flex flex-col gap-1">
          <Select value={strategy} onValueChange={(v) => onChange(v as OutreachStrategy)}>
            <SelectTrigger className="h-8 text-xs" aria-label="Message angle">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {options.map((o) => (
                <SelectItem key={o.value} value={o.value} className="text-xs">
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-muted-foreground">
            {current?.hint ||
              'On Auto the AI chooses the angle per lead. Pin one only to force it for every message in this step.'}
          </p>
        </div>
      )}
    </div>
  )
}

export function SequenceBuilder({
  steps,
  onChange,
  showSamples = false,
  hideAngle = false,
}: SequenceBuilderProps) {
  // Plan step caps (card P1): Basic 5 steps, Pro 7, Custom 9, counting the
  // connection request and first DM. The campaigns worker enforces the same,
  // with comp accounts at Custom's cap (useLimitsPlan mirrors resolveLimitsPlan).
  const plan = useLimitsPlan()
  const maxFollowUps = maxFollowUpsFor(plan)
  const maxSteps = maxSequenceStepsFor(plan)

  // Auto-correct any pre-existing campaign whose first 2 steps don't conform
  // to the lock (legacy data from before 2026-06-06). Runs once on mount and
  // any time the parent passes in a different `steps` array — idempotent
  // since the comparison short-circuits when types already match.
  useEffect(() => {
    const needsFix =
      steps.length < LOCKED_STEP_COUNT ||
      LOCKED_STEP_TYPES.some((t, i) => steps[i]?.type !== t)
    if (!needsFix) return
    const next: SequenceStep[] = [...steps]
    for (let i = 0; i < LOCKED_STEP_COUNT; i++) {
      const lockedType = LOCKED_STEP_TYPES[i]!
      if (!next[i]) {
        next[i] = {
          ...DEFAULT_STEP,
          type: lockedType,
          // Connection requests must fire on day 0; first DMs default to
          // day 0 too (they pull from the same orchestrator tick once the
          // accept reconciliation lands).
          delay_days: 0,
        }
      } else if (next[i]!.type !== lockedType) {
        next[i] = { ...next[i]!, type: lockedType }
      }
    }
    onChange(next)
    // Deps intentionally only include `steps`. `onChange` is a prop callback
    // whose identity can shift between renders without the underlying intent
    // changing — adding it would trigger this effect on every render and
    // create an update loop.
  }, [steps])

  function addStep() {
    if (steps.length >= maxSteps) return
    // If the user is adding step 1 or 2, force the locked type so the
    // sequence is always conformant even mid-build. After that, the next
    // follow-up in order (step 6 on Pro becomes Follow-up 4).
    const lockedType = DEFAULT_STEP_FOR_INDEX[steps.length]
    const next: SequenceStep = lockedType
      ? { ...DEFAULT_STEP, type: lockedType, delay_days: 0 }
      : { ...DEFAULT_STEP, type: nextFollowUpType(steps, maxFollowUps) }
    onChange([...steps, next])
  }

  function removeStep(index: number) {
    // Belt-and-suspenders: the delete button is also disabled for these
    // indices, but if anything ever bypasses the UI guard (programmatic
    // call, future refactor) we still refuse to drop the locked steps.
    if (index < LOCKED_STEP_COUNT) return
    onChange(steps.filter((_, i) => i !== index))
  }

  function updateStep(index: number, patch: Partial<SequenceStep>) {
    // Reject type changes to locked positions. delay_days / style_samples
    // are still editable on rows 1 and 2.
    if (index < LOCKED_STEP_COUNT && patch.type && patch.type !== LOCKED_STEP_TYPES[index]) {
      return
    }
    // Strategy no longer follows the step type: it stays 'auto' unless the
    // user pinned an angle under Advanced, and a pin survives a type change.
    onChange(steps.map((step, i) => (i === index ? { ...step, ...patch } : step)))
  }

  return (
    <div className="flex flex-col gap-5">
      {showSamples && (
        <div className="rounded-lg border bg-card p-3 flex flex-col gap-1">
          <Label className="text-sm font-semibold">Your writing style (optional)</Label>
          <p className="text-xs text-muted-foreground">
            Each message step below has its own box. Paste 1-3 messages you have actually written for that
            kind of touch and the AI will match your tone and cadence. Use placeholders like{' '}
            <code className="rounded bg-muted px-1 py-0.5 text-[10px] font-mono">{'{first_name}'}</code>
            {' '}or{' '}
            <code className="rounded bg-muted px-1 py-0.5 text-[10px] font-mono">{'{company}'}</code>
            {' '}where the lead's real data should go. Leave a box empty and the AI writes in a natural, plain style.
          </p>
        </div>
      )}

      {/* Timeline */}
      <div className="relative flex flex-col gap-0">
        {steps.map((step, index) => {
          const isLocked = index < LOCKED_STEP_COUNT
          return (
          <div key={index} className="relative flex gap-3">
            {/* Timeline connector */}
            <div className="flex flex-col items-center">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-cobalt-bright bg-background text-xs font-bold text-cobalt-bright">
                {index + 1}
              </div>
              {index < steps.length - 1 && (
                <div className="w-0.5 flex-1 bg-border my-1" />
              )}
            </div>

            {/* Step config */}
            <div className="flex-1 pb-4">
              <div className="rounded-lg border bg-card p-3 flex flex-col gap-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-center gap-1">
                      <Label className="text-xs">Step Type</Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex items-center text-muted-foreground hover:text-foreground"
                            aria-label="What does this step type do?"
                          >
                            <HelpCircle className="h-3 w-3" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs text-xs">
                          {STEP_TYPES.find((t) => t.value === step.type)?.hint
                            ?? 'Determines what message the AI generates for this slot in the sequence.'}
                        </TooltipContent>
                      </Tooltip>
                      {isLocked && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              className="ml-auto inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
                              aria-label="This step is locked"
                            >
                              <Lock className="h-2.5 w-2.5" />
                              Required
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-xs text-xs">
                            Every LinkedIn campaign must start with a connection request, then a first DM after the lead accepts. This is locked to keep the sequence functional.
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                    <Select
                      value={step.type}
                      onValueChange={(v) =>
                        updateStep(index, { type: v as SequenceStepType })
                      }
                      disabled={isLocked}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STEP_TYPES.filter((t) => stepTypeAllowed(t.value, maxFollowUps) || t.value === step.type).map((t) => (
                          <SelectItem key={t.value} value={t.value} className="text-xs">
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <Label className="text-xs">
                      {index === 0 ? 'Send on day' : 'Delay (days after previous)'}
                    </Label>
                    <Input
                      type="number"
                      min={0}
                      max={30}
                      step={1}
                      value={step.delay_days}
                      onChange={(e) => {
                        // Clamp to a non-negative integer in [0, 30].
                        // Number('abc') is NaN which would otherwise persist as
                        // delay_days=NaN — the backend then computes NaN for its
                        // delay window and `elapsed < NaN` is always false, so
                        // the step would fire on every 30s tick. Ugly rate-limit risk.
                        const raw = Number(e.target.value)
                        const safe = Number.isFinite(raw)
                          ? Math.max(0, Math.min(30, Math.floor(raw)))
                          : step.delay_days
                        updateStep(index, { delay_days: safe })
                      }}
                      className="h-8 text-xs"
                    />
                  </div>
                  {index > 0 && (
                    <div className="flex flex-col gap-1.5">
                      <Label className="text-xs text-[var(--color-silver)]">
                        or hours (fast test)
                      </Label>
                      <Input
                        type="number"
                        min={0}
                        max={720}
                        step={0.5}
                        placeholder="-"
                        value={step.delay_hours ?? ''}
                        onChange={(e) => {
                          // Empty clears the override (falls back to delay_days).
                          // A valid non-negative number (fractional ok) wins over
                          // delay_days in the backend eligibility gate.
                          const val = e.target.value.trim()
                          if (val === '') {
                            updateStep(index, { delay_hours: undefined })
                            return
                          }
                          const raw = Number(val)
                          if (Number.isFinite(raw) && raw >= 0) {
                            updateStep(index, { delay_hours: Math.min(720, raw) })
                          }
                        }}
                        className="h-8 w-24 text-xs"
                        title="Overrides the day delay with an hour delay. Leave blank for normal day-based cadence. Used for compressed test runs (e.g. 3 = wait 3 hours)."
                      />
                    </div>
                  )}
                </div>

                {/* Optional per-step angle pin — collapsed; Auto by default. */}
                {!hideAngle && (
                  <StepAngleControl
                    strategy={step.strategy}
                    onChange={(strategy) => updateStep(index, { strategy })}
                  />
                )}

                <div className="flex justify-end">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                    onClick={() => removeStep(index)}
                    disabled={isLocked || steps.length <= 1}
                    aria-label={
                      isLocked
                        ? `Step ${index + 1} is required and cannot be removed`
                        : `Remove step ${index + 1}`
                    }
                    title={
                      isLocked
                        ? `Step ${index + 1} is required and cannot be removed`
                        : undefined
                    }
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>

                {/* Per-step writing samples. The connection request goes out
                    as a plain invite with no note, so it gets no box (unless
                    an older campaign already saved samples there). */}
                {showSamples &&
                  (step.type !== 'connection_request' || (step.style_samples ?? []).length > 0) && (
                  <div className="border-t pt-3">
                    <StyleSamplesEditor
                      label={`${STEP_TYPES.find((t) => t.value === step.type)?.label ?? step.type} samples (optional)`}
                      hint="Paste up to 3 messages you have actually written for this kind of touch."
                      samples={step.style_samples ?? []}
                      onChange={(style_samples) => updateStep(index, { style_samples })}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          )
        })}
      </div>

      {/* Add step */}
      {steps.length < maxSteps && (
        <Button variant="outline" size="sm" onClick={addStep} className="self-start">
          <Plus className="h-4 w-4 mr-1" />
          Add Step
        </Button>
      )}

      {steps.length >= maxSteps && (
        <p className="text-xs text-muted-foreground">
          Maximum of {maxSteps} steps on your plan ({maxFollowUps} follow-ups after the first DM).
          {plan === 'starter' && ` Pro allows ${maxSequenceStepsFor('pro')}.`}
        </p>
      )}
    </div>
  )
}
